import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Route prefixes that are considered "internal" — dark mode is allowed here.
 * All other routes (landing, auth, invite, pay, etc.) are always light.
 */
const DARK_MODE_ALLOWED_PREFIXES = [
  '/dashboard',
  '/portal/owner',
  '/portal/tenant',
  '/portal/employee',
  '/portal/provider',
  '/super-admin',
];

function isInternalRoute(pathname: string): boolean {
  return DARK_MODE_ALLOWED_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * Safe localStorage wrapper that handles null localStorage in strict environments
 */
function safeLocalStorage() {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage;
    }
  } catch {
    // localStorage access can throw in some privacy modes
  }
  return null;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Light mode is the DEFAULT - only use dark if explicitly saved
  const [theme, setTheme] = useState<Theme>(() => {
    const storage = safeLocalStorage();
    if (storage) {
      try {
        const saved = storage.getItem('dallali-theme') as Theme;
        if (saved === 'dark') return 'dark';
      } catch {
        // getItem can throw in some privacy/security modes
      }
    }
    return 'light';
  });

  const location = useLocation();

  useEffect(() => {
    const root = document.documentElement;
    // Only apply dark class when user is inside an internal dashboard/portal route
    const applyDark = theme === 'dark' && isInternalRoute(location.pathname);
    root.classList.toggle('dark', applyDark);
  }, [theme, location.pathname]);

  const toggleTheme = () => {
    setTheme((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      const storage = safeLocalStorage();
      if (storage) {
        try {
          storage.setItem('dallali-theme', next);
        } catch {
          // setItem can throw when storage is full or access is denied
        }
      }
      return next;
    });
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};
